import {ChartComponent} from './ChartComponent.js';
import {ChartDataBuilder} from './ChartDataBuilder.js';
import TwoButtonsDialog from '../../dialog/TwoButtonsDialog.js';
import StatisticComponents from './StatisticComponents.js';
import {safeHtml} from '../../../util/CommonUtils.js';
import MouseBlockingUnderlay from '../../MouseBlockingUnderlay.js';
import {COLUMN_TYPE} from "../TableColumn.js";
import LineConfig from "../LineConfig.js";

export class VisualizationChartComponent {
	#callbacks = {
		onSave: () => {},
		onSelectionChanged: () => {},
	};
	/**
	 * @param {Element} parentNode
	 * @param {VizData} vizData
	 * @param {VisualizationDataSelection} selection
	 * @param {VisualizationProperties} props
	 * @param {RegressionApiClient} regressionApiClient
	 */
	constructor(parentNode, vizData, selection, props, regressionApiClient) {
		this._props = props;
		this._root = this._render(parentNode);
		this._vizData = vizData;
		this._regressionApiClient = regressionApiClient;
		this.axisX = new AxisLabelComponent(this._root.querySelector('.x-axis-col'), props.chart.axes.x, 'x-axis-label')
		this.axisY = new AxisLabelComponent(this._root.querySelector('.y-axis-col'),  props.chart.axes.y, 'y-axis-label')
		this.colorProperty = new AxisLabelComponent(this._root.querySelector('.axis-color'),  props.chart.color, 'z-axis-label')
		this.sizeProperty = new AxisLabelComponent(this._root.querySelector('.axis-size'),  props.chart.size, 'z-axis-label')
		this.connectionsProperty = new AxisLabelComponent(this._root.querySelector('.connection-property'),  props.chart.connections, 'z-axis-label')
		this._chartDataBuilder = new ChartDataBuilder(vizData);
		this._chart = new ChartComponent(this._root.querySelector('.vis-chart-container'), props, selection,
			this._chartDataBuilder);
		this._chart.onSelectionChanged((data)=>{
			this.#callbacks.onSelectionChanged(data)
		});
		this.statisticComponents = new StatisticComponents(this._chart._statisticDrawer, this._chartDataBuilder);
		this.statisticComponents.on('change', async () => {
			this._props.chart.lines = this.statisticComponents.toServerJson();
			await this.#callbacks.onSave();
		});
		[this.axisX, this.axisY, this.colorProperty, this.sizeProperty, this.connectionsProperty].forEach(axis=>{
			axis.on('dialog-open', (axis)=>this.openAxisPropertiesDialog([axis]))
			axis.on('change', async ()=> {
				switch (axis) {
					case this.axisX : {
						this._props.chart.axes.x = axis.getName();
						break;
					}
					case this.axisY : {
						this._props.chart.axes.y = axis.getName();
						break;
					}
					case this.colorProperty : {
						this._props.chart.color = axis.getName();
						break;
					}
					case this.sizeProperty : {
						this._props.chart.size = axis.getName();
						break;
					}
					case this.connectionsProperty : {
						this._props.chart.connections = axis.getName();
						break;
					}
				}
				await this.draw();
				await this.#callbacks.onSave();
			})
		})
	}

	/**
	 * @param {function} cb
	 */
	onSave(cb) {
		this.#callbacks.onSave = cb;
	}

	onSelectionChanged(cb) {
		this.#callbacks.onSelectionChanged = cb;
	}

	draw() {
		const points = this._chartDataBuilder.build({
				x: this.axisX.getName(),
				y: this.axisY.getName(),
				color: this.colorProperty.getName(),
				size: this.sizeProperty.getName(),
				connections: this.connectionsProperty.getName()
			}
		);
		this._chart.updateData(points);
		this.statisticComponents.updateAll()
	}

	isValidAxes() {
		let columns = this._vizData.getColumns();
		if(this.axisX.getName() !== null && !columns.hasName(this.axisX.getName())){
			this.axisX.setName(null, false);
			this._props.chart.axes.x = null
		}
		if(this.axisY.getName() !== null && !columns.hasName(this.axisY.getName())){
			this.axisY.setName(null, false);
			this._props.chart.axes.y = null
		}
		if(this.sizeProperty.getName() !== null && !columns.hasName(this.sizeProperty.getName())){
			this.sizeProperty.setName(null, false);
			this._props.chart.size = null
		}
		if(this.colorProperty.getName() !== null && !columns.hasName(this.colorProperty.getName())){
			this.colorProperty.setName(null, false);
			this._props.chart.color = null
		}
		if(this.connectionsProperty.getName() !== null && !columns.hasName(this.connectionsProperty.getName())){
			this.connectionsProperty.setName(null, false);
			this._props.chart.connections = null
		}
		return this.axisX.getName() !== null && this.axisY.getName() !== null
	}

	/**
	 * @param {FilterCriteria} filterCriteria
	 */
	setRowsFilter(filterCriteria) {
		this._vizData.setFilter(filterCriteria);
	}

	showStatisticOptionsDialog(){
		let dialog = new TwoButtonsDialog();
		dialog.render(`<table>
						 <tbody>
						   <tr>
						     <td>
						       <ul data-type="distribution">
						         <li><input id="mean" type="checkbox"><label for="mean">Mean</label></li>
								 <li><input id="sd1" type="checkbox"><label for="sd1">&pm;1 SD</label></li>
								 <li><input id="sd2" type="checkbox"><label for="sd2">&pm;2 SD</label></li>
								 <li><input id="sd3" type="checkbox"><label for="sd3">&pm;3 SD</label></li>
							   </ul>
							 </td>
							 <td>
						       <ul data-type="percentile">
						         <li><input id="p5" type="checkbox"><label for="p5">5th percentile</label></li>
								 <li><input id="p25" type="checkbox"><label for="p25">Q1</label></li>
								 <li><input id="p50" type="checkbox"><label for="p50">Median</label></li>
								 <li><input id="p75" type="checkbox"><label for="p75">Q3</label></li>
								 <li><input id="p95" type="checkbox"><label for="p95">95th percentile</label></li>
							   </ul>
							 </td>
							 <td>
						       <ul data-type="regression">
						         <li><input id="fit-curve" type="checkbox"><label for="fit-curve">Fit curve</label></li>
							   </ul>
							 </td>
						   </tr>
						 </tbody>
					   </table>`, 'chart-statistics-options')
		dialog.on('ok', async () => {
			for (const input of [...dialog._dialog.querySelectorAll('input:checked')])
				await this.statisticComponents.create(LineConfig.parseServerJson(VisualizationChartComponent.FUNCTIONS[input.id]), this._regressionApiClient)
			this._props.chart.lines = this.statisticComponents.toServerJson();
			await this.#callbacks.onSave();
		})
	}

	/** @param {AxisLabelComponent[]} axisComponent */
	openAxisPropertiesDialog(axisComponent) {
		const names = [null];
		for (const column of this._vizData.getColumns())
			if (column.getType() !== COLUMN_TYPE.SVG)
				names.push(column.getDisplayName())
		// If array of AxisComponents contain 2 elements, it's mean that two axes are invalid
		// and after select first axis, we should open this dialog for another axis
		if (axisComponent[1]){
			axisComponent[0].showOptionsDropdown(names, axisComponent[0].getName(),
				()=>{axisComponent[1].showOptionsDropdown(names, axisComponent[1].getName())});
		} else {
			axisComponent[0].showOptionsDropdown(names, axisComponent[0].getName());
		}
	}

	_render(parentNode) {
		let template = document.createElement('template');
		template.innerHTML = `<div class="vis-chart-component">
								<div class="y-axis-col"></div>
								<div class="x-axis-col"></div>
								<div class="vis-chart-container"></div>
								<div class="vis-chart-data-options">
									<ul>
										<li><span class="option-name">Color</span><div class="axis-color"></div></li>
										<li><span class="option-name">Size</span><div class="axis-size"></div></li>
										<li><span class="option-name">Connect</span><div class="connection-property"></div></li>
									</ul>
								</div>
							  </div>`;
		parentNode.appendChild(template.content.cloneNode(true))
		return parentNode.lastChild;
	}

	// We changed type of params.percentile and params.numOfSd from string to number, because we also save value
	// from the corresponding input of the Statistic dialog with the number type.
	// But in the DB the previous saved values have string type
	static FUNCTIONS = {
		mean: {lineFunction: {type: "mean", params: {}, labelPattern: "Mean \${value}"}, lineStyle : {type:'solid', color:'rgb(51, 160, 43)'}},
		sd1: {lineFunction: {type: "sd", params: {numOfSd:1}, labelPattern: "1SD \${value}"}, lineStyle : {type:'dashed', color:'rgb(227, 33, 28)'}},
		sd2: {lineFunction: {type: "sd", params: {numOfSd:2}, labelPattern: "2SD \${value}"}, lineStyle : {type:'dashed', color:'rgb(227, 33, 28)'}},
		sd3: {lineFunction: {type: "sd", params: {numOfSd:3}, labelPattern: "3SD \${value}"}, lineStyle : {type:'dashed', color:'rgb(227, 33, 28)'}},
		p5: {lineFunction: {type: "percentile", params: {percentile:0.05}, labelPattern: "5th \${value}"}, lineStyle : {type:'dotted', color:'rgb(106, 61, 154)'}},
		p25: {lineFunction: {type: "percentile", params: {percentile:0.25}, labelPattern: "Q1 \${value}"}, lineStyle : {type:'dotted', color:'rgb(106, 61, 154)'}},
		p50: {lineFunction: {type: "percentile", params: {percentile:0.5}, labelPattern: "Median \${value}"}, lineStyle : {type:'dotted', color:'rgb(106, 61, 154)'}},
		p75: {lineFunction: {type: "percentile", params: {percentile:0.75}, labelPattern: "Q3 \${value}"}, lineStyle : {type:'dotted', color:'rgb(106, 61, 154)'}},
		p95: {lineFunction: {type: "percentile", params: {percentile:0.95}, labelPattern: "95th \${value}"}, lineStyle : {type:'dotted', color:'rgb(106, 61, 154)'}},
		"fit-curve": {lineFunction: {type: "regression", params: {}, labelPattern: ""}, lineStyle : {type:'solid', color:'rgb(31, 120, 180)'}}
	};
}

export class AxisLabelComponent {
	constructor(parentNode, text, cssClass) {
		this._element = this._render(parentNode, cssClass)
		this._element.addEventListener('click', () => {
			if (typeof this._callbacks['dialog-open'] === 'function')
				this._callbacks['dialog-open'](this)
		});
		this._element.querySelector('.axis-options-dropdown').addEventListener('click', (evt) => {
			evt.stopPropagation()
		})
		this._callbacks = {};
		this.setName(text);
	}

	setName(text = null, isNeedToCallChangeCallback = true) {
		let el = this._element.querySelector('span');
		if (text === null) {
			this._element.title = "None"
			el.textContent = "None";
			delete el.dataset.name;
		} else {
			this._element.title = text
			el.textContent = text;
			el.dataset.name = text;
		}
		if (isNeedToCallChangeCallback && typeof this._callbacks['change'] === 'function')
			this._callbacks['change'](this)
	}

	getName(){
		let name = this._element.querySelector('span').dataset.name;
		return name? name.trim() : null;
	}

	on(eventName, cb){
		this._callbacks[eventName] = cb;
	}

	showOptionsDropdown(options, selectedOption, callBack) {
		let list = this._element.querySelector('.md-list');
		list.innerHTML = '';
		let dropdown = this._element.querySelector('.axis-options-dropdown');
		for (let option of options) {
			let template = document.createElement('template');
			template.innerHTML = `<li class="md-list-item"> 
                            			<div class="md-list-item-content"> 
                              				<span class="md-list-item__text"></span> 
                            			</div>
                          			  </li>`
			list.appendChild(template.content);
			let li = list.lastChild;
			let text = option;
			if (option === selectedOption)
				li.classList.add('selected')
			if (option !== null)
				li.dataset.name = option;
			else
				text = 'None'
			li.title = text;
			li.querySelector('span').textContent = text;
			li.addEventListener('click', async () => {
				this.setName(option)
				dropdown.toggleAttribute('visible');
				document.querySelector('.mouse-blocking-overlay').remove()
				if (callBack)
					callBack()
			})
		}
		dropdown.toggleAttribute('visible');
		MouseBlockingUnderlay.show(() => {
			dropdown.toggleAttribute('visible')
		})
		if (this._element.classList.contains('z-axis-label')) return
		let outer = this._element.parentNode.parentNode.getBoundingClientRect();
		dropdown.style.maxWidth = outer.width - 36 - Math.max(0, outer.right - window.innerWidth) + 'px';
		dropdown.style.maxHeight = outer.height - 36 - Math.max(0, outer.bottom - window.innerHeight) + Math.min(0, outer.top - 64) + 'px';
		let rect = dropdown.getBoundingClientRect();
		if (this._element.classList.contains('x-axis-label')) {
			dropdown.style.top = outer.height - rect.height - 36 + 'px';
			dropdown.style.left = (outer.width / 2) - (rect.width / 2) + 'px';
		} else
			dropdown.style.top = (outer.height - Math.max(0, outer.bottom - window.innerHeight) - Math.min(0, outer.top - 64)) / 2 - rect.height / 2 - 16 + 'px';
	}

	_render(parentNode, cssClass) {
		let template = document.createElement('template');
		template.innerHTML = safeHtml`<div class="${cssClass}">
										<div class="name">
										  <span id="name"></span>
										  <div class="dropdown-triangle-area">
										    <div class="dropdown-triangle"></div>
										  </div>
										</div>
										<div class="md-list-wrapper dropdown-menu axis-options-dropdown"> 
	                       				  <ul class="md-list"></ul>
                      					</div>
									  </div>`
		parentNode.appendChild(template.content.cloneNode(true))
		return parentNode.lastChild
	}
}